import { useEffect, useRef, useState, type RefObject } from "react";
import { Button, EmptyState, Field, FormDialog, FormRow, Picker, RecordList, Spinner, Tag, Textarea, DialogCancel } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { groupLoyalty, loyaltyLabel, LOYALTY_KINDS, type LoyaltyKind } from "../contacts/travelFormat.js";
import {
  useMyProfile,
  usePublicProfile,
  useUpdateProfile,
  useUpdateTravel,
  useUpdatePayment,
  usePersonTravel,
  useSkillsCatalog,
  useSetAvatar,
  type ProfileView,
  type PublicProfileView,
} from "../../app/hooks/useProfile.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { AvatarEditor } from "../shared/AvatarEditor.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import styles from "./ProfileModal.module.scss";

export interface ProfileModalProps {
  /** Present ⇒ VIEW another person's public profile (no travel). Absent ⇒ edit the caller's own profile. */
  personId?: string;
  /** `onboarding` reframes edit-own as invite-acceptance setup (same endpoints, a born-blank profile). */
  mode?: "onboarding";
  onClose: () => void;
}

/** Two initials for the avatar fallback (born-blank → "?"). */
function initialsOf(p: { firstName?: string | null; lastName?: string | null }): string {
  const s = `${p.firstName?.trim()?.[0] ?? ""}${p.lastName?.trim()?.[0] ?? ""}`.toUpperCase();
  return s || "?";
}

/** One editable loyalty row — the typed airline/hotel entry (ART-197), kept as strings while editing. */
interface LoyaltyRow {
  kind: LoyaltyKind;
  name: string;
  number: string;
}

/**
 * Personal profile modal (the routed `/profile` modal). THREE modes driven by props:
 *   • edit-own — `GET /me/profile` preload → `PUT /me/profile` + `PUT /me/travel` (name/phone/bio/location/
 *     skills[] + the travel block).
 *   • onboarding — the same edit-own form reframed for invite acceptance (born-blank profile).
 *   • view-others — `personId` set → `GET /users/{id}/profile` (PublicProfileView, NO travel); a 404 renders
 *     the designed "not available" state.
 * A `FormDialog`: a Modal on desktop, a bottom sheet on a phone (ART-251 — a centred card with the keyboard up
 * covers the fields). Every save surfaces the server error verbatim inline and NEVER reports
 * success on an unpersisted write (the skills exact-match 400 stays on-screen).
 */
export function ProfileModal({ personId, mode, onClose }: ProfileModalProps) {
  const isView = personId != null && personId !== "";
  const firstFocusRef = useRef<HTMLInputElement>(null);

  // Dialog focus management: focus in, TRAP Tab, RESTORE to trigger on close.
  return (
    <FormDialog
      title={isView ? "Profile" : mode === "onboarding" ? "Welcome — set up your profile" : "Your profile"}
      onClose={onClose}
    >

        {isView ? (
          <PublicView personId={personId as string} />
        ) : (
          <OwnProfileForm mode={mode} onClose={onClose} firstFieldRef={firstFocusRef} />
        )}
    </FormDialog>
  );
}

// ---- view-others ---------------------------------------------------------------------------------------

function PublicView({ personId }: { personId: string }) {
  const q = usePublicProfile(personId);

  if (q.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading profile…" />
      </div>
    );
  }
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <div className={styles.body}>
        {notFound ? (
          <EmptyState title="Profile not available" description="This profile isn't available or has been removed." />
        ) : (
          <EmptyState
            title="Couldn't load this profile"
            description={q.error instanceof Error ? q.error.message : "Please try again."}
            action={<Button onClick={() => void q.refetch()}>Retry</Button>}
          />
        )}
      </div>
    );
  }

  const p = q.data as PublicProfileView;
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "Unnamed";
  return (
    <div className={styles.body}>
      <div className={styles.identity}>
        <PersonAvatar personId={p.personId} avatarMediaId={p.avatarMediaId} initials={initialsOf(p)} size="xl" />
        <div>
          <p className={styles.viewName}>{name}</p>
          {p.location && <p className={styles.viewMeta}>{p.location}</p>}
        </div>
      </div>
      {p.bio && <p className={styles.viewBio}>{p.bio}</p>}
      <ReadOnlyChips label="Skills" items={p.skills} />
      <TravelSection personId={personId} />
    </div>
  );
}

/**
 * Travel & Loyalty — the money team's view of a crew member's travel identifiers.
 *
 * Renders NOTHING unless the server hands the block over. A viewer who may not look gets a 404, and the
 * right response to that is silence: there is no action to offer, and "you don't have permission to see
 * this person's travel documents" is itself a disclosure about them. So no error state, no empty state, no
 * placeholder — the section simply isn't there.
 *
 * Read-only by design. Only the person edits their own, on their own profile.
 */
function TravelSection({ personId }: { personId: string }) {
  const q = usePersonTravel(personId);
  const t = q.data;
  if (!t) return null;

  const { airlines, hotels } = groupLoyalty(t.loyaltyPrograms);
  const rows: { label: string; value: string }[] = [
    ...(t.knownTravelerNumber ? [{ label: "Known Traveler", value: t.knownTravelerNumber }] : []),
    ...(t.redressNumber ? [{ label: "Redress", value: t.redressNumber }] : []),
    ...(airlines.length > 0 ? [{ label: "Airlines", value: airlines.map(loyaltyLabel).join(", ") }] : []),
    ...(hotels.length > 0 ? [{ label: "Hotels", value: hotels.map(loyaltyLabel).join(", ") }] : []),
  ];
  // The block exists but the person has filled nothing in — say so, rather than rendering an empty heading.
  // This is a DIFFERENT state from "not permitted" (which never reaches here at all).
  if (rows.length === 0) {
    return (
      <div className={styles.section}>
        <p className={styles.sectionLabel}>Travel &amp; Loyalty</p>
        <p className={styles.viewMeta}>Nothing recorded yet.</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>Travel &amp; Loyalty</p>
      {rows.map((r) => (
        <div key={r.label} className={styles.travelRow}>
          <span className={styles.travelLabel}>{r.label}</span>
          <span className={styles.travelValue}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function ReadOnlyChips({ label, items }: { label: string; items?: string[] | null }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>{label}</p>
      <div className={styles.chips}>
        {items.map((it) => (
          <Tag key={it} tone="muted">
            {it}
          </Tag>
        ))}
      </div>
    </div>
  );
}

// ---- edit-own / onboarding ----------------------------------------------------------------------------

function OwnProfileForm({
  mode,
  onClose,
  firstFieldRef,
}: {
  mode?: "onboarding";
  onClose: () => void;
  firstFieldRef: RefObject<HTMLInputElement | null>;
}) {
  const q = useMyProfile();
  const updateProfile = useUpdateProfile();
  const updateTravel = useUpdateTravel();
  const updatePayment = useUpdatePayment();
  const catalog = useSkillsCatalog();
  const me = q.data as ProfileView | undefined;
  const setAvatar = useSetAvatar(me?.personId);
  const avatarUrl = useAvatarUrl("person", me?.personId, me?.avatarMediaId);

  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", bio: "", location: "" });
  const [skills, setSkills] = useState<string[]>([]);
  const [travel, setTravel] = useState({ ktn: "", redress: "" });
  const [venmo, setVenmo] = useState("");
  const [loyalty, setLoyalty] = useState<LoyaltyRow[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hydrated || !q.data) return;
    const p = q.data as ProfileView;
    setForm({
      firstName: p.firstName ?? "",
      lastName: p.lastName ?? "",
      phone: p.phone ?? "",
      bio: p.bio ?? "",
      location: p.location ?? "",
    });
    setSkills(p.skills ?? []);
    setTravel({
      ktn: p.travel?.knownTravelerNumber ?? "",
      redress: p.travel?.redressNumber ?? "",
    });
    setVenmo(p.payment?.venmoUsername ?? "");
    setLoyalty(
      (p.travel?.loyaltyPrograms ?? [])
        .filter((l): l is { kind: LoyaltyKind; name?: string | null; number?: string | null } =>
          l?.kind === "airline" || l?.kind === "hotel")
        .map((l) => ({ kind: l.kind, name: l.name ?? "", number: l.number ?? "" })),
    );
    setHydrated(true);
  }, [hydrated, q.data]);

  if (q.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading your profile…" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className={styles.body}>
        <EmptyState
          title="Couldn't load your profile"
          description={q.error instanceof Error ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  const saving = updateProfile.isPending || updateTravel.isPending || updatePayment.isPending;

  async function onSave() {
    setError(null);
    try {
      // PUT /me/profile — the server validates skills against its catalog; an invalid skill 400s and we surface
      // the message verbatim (no client-side skill list).
      await updateProfile.mutateAsync({
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        phone: form.phone.trim() || null,
        bio: form.bio.trim() || null,
        location: form.location.trim() || null,
        skills,
      });
      // PUT /me/travel — the mobile travel block. Loyalty is the typed airline/hotel list (ART-197); drop
      // rows with no program name (an added-but-unfilled row), and send an empty number as null.
      await updateTravel.mutateAsync({
        knownTravelerNumber: travel.ktn.trim() || null,
        redressNumber: travel.redress.trim() || null,
        loyaltyPrograms: loyalty
          .map((r) => ({ kind: r.kind, name: r.name.trim(), number: r.number.trim() || null }))
          .filter((r) => r.name.length > 0),
      });
      // PUT /me/payment — the Venmo handle (ART-241). Stored without '@'; the server strips a pasted one and
      // validates the charset, surfacing a 400 verbatim. Blank clears it.
      await updatePayment.mutateAsync({ venmoUsername: venmo.trim() || null });
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Couldn't save your profile.");
    }
  }

  return (
    <div className={styles.body}>
      {mode === "onboarding" && (
        <p className={styles.onboarding}>Tell your team who you are — you can change any of this later.</p>
      )}

      {/* The picture, top-middle (PO reference, 2026-08-20). The control itself is `AvatarEditor`, shared
          with the chat-avatar surface since 2026-09-11 — the treatment is this one, lifted verbatim, so the
          two cannot drift. The UPLOAD stays here, because a person's avatar is a multipart PUT /me/avatar
          and a chat's is not; what is shared is the gesture. */}
      <AvatarEditor
        src={avatarUrl}
        placeholder={initialsOf({ firstName: form.firstName, lastName: form.lastName })}
        label="Replace profile picture"
        busy={setAvatar.isPending}
        onPicked={(file) =>
          setAvatar.mutate(file, {
            onError: (err) =>
              setError(err instanceof Error && err.message ? err.message : "Couldn't upload the picture."),
          })
        }
      />

      <FormRow>
        <Field ref={firstFieldRef} label="First name" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
        <Field label="Last name" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
      </FormRow>
      <FormRow>
        <Field label="Phone" type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        <Field label="Location" placeholder="City, State" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
      </FormRow>

      <Textarea label="Bio" rows={3} value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} />

      {/* Skills come FROM the catalog (the PO-reported "guitar" failure, 2026-08-20: free text against an
          exact-match catalog guaranteed mystery 400s). The type-ahead offers exactly what a save accepts;
          value stays "" so each pick ADDS a chip rather than selecting. */}
      <div className={styles.section}>
        <p className={styles.sectionLabel}>Skills</p>
        {skills.length > 0 && (
          <div className={styles.chips}>
            {skills.map((it) => (
              <Tag key={it} tone="muted" onRemove={() => setSkills((xs) => xs.filter((x) => x !== it))}>
                {it}
              </Tag>
            ))}
          </div>
        )}
        <Picker
          bare
          typeahead
          label="Add skill"
          placeholder="Type a skill…"
          value=""
          triggerClassName={styles.tokenInput}
          options={(catalog.data ?? [])
            .filter((sk) => !skills.includes(sk))
            .map((sk) => ({ value: sk, label: sk }))}
          onChange={(sk) => setSkills((xs) => (xs.includes(sk) ? xs : [...xs, sk]))}
        />
      </div>

      <fieldset className={styles.travel}>
        <legend className={styles.sectionLabel}>Travel</legend>
        <FormRow>
          <Field label="Known Traveler No." value={travel.ktn} onChange={(e) => setTravel((t) => ({ ...t, ktn: e.target.value }))} />
          <Field label="Redress No." value={travel.redress} onChange={(e) => setTravel((t) => ({ ...t, redress: e.target.value }))} />
        </FormRow>

        {/* Loyalty programs — typed airline/hotel rows (ART-197). Each row picks a kind, names the program and
            (optionally) its membership number; the card groups them into Airlines and Hotels on read. The order
            is the array order, which the save sends as-is. */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Loyalty programs</p>
          <RecordList
            dragKind="profile-loyalty"
            items={loyalty}
            addLabel="Add program"
            nameOf={(_, i) => `program ${i + 1}`}
            onAdd={() => setLoyalty((xs) => [...xs, { kind: "airline", name: "", number: "" }])}
            onMove={(from, to) =>
              setLoyalty((xs) => {
                const next = [...xs];
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved!);
                return next;
              })
            }
            onRemove={(i) => setLoyalty((xs) => xs.filter((_, j) => j !== i))}
            renderFields={(row, i) => {
              const patch = (changes: Partial<LoyaltyRow>) =>
                setLoyalty((xs) => xs.map((r, j) => (j === i ? { ...r, ...changes } : r)));
              return (
                <>
                  <Picker
                    hideLabel
                    label={`Program ${i + 1} type`}
                    value={row.kind}
                    onChange={(v) => patch({ kind: v as LoyaltyKind })}
                    options={LOYALTY_KINDS.map((k) => ({ value: k.value, label: k.label }))}
                  />
                  <Field
                    hideLabel
                    label={`Program ${i + 1} name`}
                    placeholder="Program (e.g. Delta SkyMiles)"
                    value={row.name}
                    onChange={(e) => patch({ name: e.target.value })}
                  />
                  <Field
                    hideLabel
                    label={`Program ${i + 1} number`}
                    placeholder="Number"
                    value={row.number}
                    onChange={(e) => patch({ number: e.target.value })}
                  />
                </>
              );
            }}
          />
        </div>
      </fieldset>

      {/* Payment (ART-241) — the member's own Venmo handle, so a manager can pay per diems + day pay straight
          to it. Stored without '@'; the server strips a pasted one. Only the person edits this. */}
      <fieldset className={styles.travel}>
        <legend className={styles.sectionLabel}>Payment</legend>
        <FormRow>
          <Field
            label="Venmo username"
            placeholder="username (no @)"
            value={venmo}
            onChange={(e) => setVenmo(e.target.value)}
          />
        </FormRow>
        <p className={styles.hint}>Your manager can pay per diems and day pay straight to this account.</p>
      </fieldset>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <footer className={styles.footer}>
        <DialogCancel />
        <Button onClick={() => void onSave()} loading={saving}>
          {mode === "onboarding" ? "Get started" : "Save profile"}
        </Button>
      </footer>
    </div>
  );
}

